import { Box } from '@mui/material'
import { CarrierType, CarrierVisitDirection } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useTranslate } from '@tolgee/react'
import { TomTable, useMinimalsTheme, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { BaplieParseResult } from '../Order/stores/OrderListUploadParsingStore'
import { RestowListMenuOptions } from './Components/molecules/RestowListMenuOptions'
import { VesselVisitOrderListHeader } from './Components/molecules/VesselVisitOrderListHeader'
import { OrderListUploadDialogV2 } from './Components/Upload/OrderListUploadDialog'
import {
  RestowListTableColumnsDto,
  VesselVisitRestowListViewStore,
} from './Stores/VesselVisitOrderListViewStore'
import { getRestowListColumns } from './tableConfigs/restowListColumns'

type Props = {
  vesselVisitItem: IVesselVisitItem
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
}

export const VesselVisitRestowListPage: FC<Props> = withAuthenticationRequired(
  observer(({ vesselVisitItem, parseBaplie }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { orderListUploadViewStoreV2 } = usePlanningStore()

    const vesselVisitRestowListViewStore = useMemo(() => {
      return new VesselVisitRestowListViewStore(vesselVisitItem)
    }, [vesselVisitItem])

    const columns = useMemo(() => getRestowListColumns(t), [t])

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'planning/vessel-visit-restow',
    )

    const table = useTomTable<RestowListTableColumnsDto>(
      {
        columns,
        data: vesselVisitRestowListViewStore.ordersTableData,
        getRowId: row => row.id.toString(),

        enableTopToolbar: false,
        enableExpanding: false,
        enableExpandAll: false,
        muiTableContainerProps: {
          sx: {
            height: 'calc(100vh - 210px)',
          },
        },

        renderRowActions: ({ row }) => {
          const orderId = row.original.id
          const journey = vesselVisitItem?.restow?.dischargeOrders.find(
            o => o.id === orderId,
          )?.containerJourney

          return RestowListMenuOptions({ journey })
        },
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    const onUploadClick = async () => {
      const visitId = vesselVisitItem?.id

      orderListUploadViewStoreV2.reset()

      await orderListUploadViewStoreV2.setVesselVisitId(visitId)
      orderListUploadViewStoreV2.setIsRestow(true)
      orderListUploadViewStoreV2.setDirection(CarrierVisitDirection.Inbound)

      orderListUploadViewStoreV2.setOpen(true)
    }

    useEffect(() => {
      vesselVisitRestowListViewStore.setIsFullView(table, false)
    }, [table, vesselVisitRestowListViewStore])

    return (
      <>
        {vesselVisitItem && (
          <VesselVisitOrderListHeader
            onUploadClick={onUploadClick}
            vesselVisitItem={vesselVisitItem}
            direction='restow'
          />
        )}
        <Box
          sx={{
            bgcolor: theme.palette.grey[100],
            padding: theme.customSpacing.m,
          }}
        >
          <TomTable table={table} />
        </Box>
        <OrderListUploadDialogV2
          enableFieldSelectionFromOrdersView={false}
          open={orderListUploadViewStoreV2.open}
          handleDialogClose={() => {
            orderListUploadViewStoreV2.setOpen(false)
          }}
          visitType={CarrierType.Vessel}
          parseBaplie={parseBaplie}
        />
      </>
    )
  }),
)
